import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { toast } from 'sonner';
import { Crown, Search, Loader2, Package, X, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Card } from '../ui/card';
import { Switch } from '../ui/switch';
import { Product } from '../../types';
import { fetchAllProducts } from '../../services/productService';

export interface RushdTreatFormData {
  productId: string;
  productName: string;
  productImage?: string | null;
  quantity: number;
  isActive: boolean;
}

interface CreateRushdTreatModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialTreat?: RushdTreatFormData | null;
  onSave: (data: RushdTreatFormData) => Promise<void>;
}

export function CreateRushdTreatModal({ isOpen, onClose, initialTreat, onSave }: CreateRushdTreatModalProps) {
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [quantity, setQuantity] = useState('1');
  const [isActive, setIsActive] = useState(true);
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showProductPicker, setShowProductPicker] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    const loadProducts = async () => {
      try {
        setLoadingProducts(true);
        const allProducts = await fetchAllProducts();
        const freeProducts = allProducts.filter(product => !product.disabled && product.price === 0);
        setProducts(freeProducts);
        setFilteredProducts(freeProducts);
      } catch (error) {
        console.error('Error loading products:', error);
        toast.error('Failed to load products');
      } finally {
        setLoadingProducts(false);
      }
    };

    loadProducts();
  }, [isOpen]);

  useEffect(() => {
    if (initialTreat && isOpen) {
      setSelectedProduct(initialTreat.productId ? {
        id: initialTreat.productId,
        name: initialTreat.productName,
        image: initialTreat.productImage || undefined,
        price: 0,
        description: '',
        images: [],
        categories: [],
        disabled: false,
        stock: 0,
        variations: [],
        sku: '',
        tags: [],
      } as Product : null);
      setQuantity(String(initialTreat.quantity || 1));
      setIsActive(initialTreat.isActive ?? true);
    } else if (!initialTreat && isOpen) {
      setSelectedProduct(null);
      setQuantity('1');
      setIsActive(true);
      setShowProductPicker(true);
    }
  }, [initialTreat, isOpen]);

  useEffect(() => {
    if (isOpen && initialTreat) {
      setShowProductPicker(false);
    }
  }, [isOpen, initialTreat]);

  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredProducts(products);
    } else {
      const lower = searchQuery.toLowerCase();
      setFilteredProducts(products.filter(product =>
        product.name.toLowerCase().includes(lower) ||
        product.category?.toLowerCase().includes(lower) ||
        product.brand?.toLowerCase().includes(lower)
      ));
    }
  }, [searchQuery, products]);

  const handleSelectProduct = (product: Product) => {
    setSelectedProduct(product);
    setShowProductPicker(false);
    setSearchQuery('');
  };

  const handleClose = () => {
    if (saving) return;
    onClose();
    setTimeout(() => {
      setSelectedProduct(null);
      setQuantity('1');
      setIsActive(true);
      setSearchQuery('');
      setShowProductPicker(false);
    }, 200);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedProduct) {
      toast.error('Select a free item to treat Rush\'d members');
      return;
    }

    const quantityValue = parseInt(quantity, 10);
    if (isNaN(quantityValue) || quantityValue < 1) {
      toast.error('Quantity must be at least 1');
      return;
    }

    setSaving(true);
    try {
      await onSave({
        productId: selectedProduct.id,
        productName: selectedProduct.name,
        productImage: selectedProduct.image || selectedProduct.images?.[0] || null,
        quantity: quantityValue,
        isActive,
      });
      handleClose();
    } catch (error) {
      console.error('Error saving Rush\'d treat:', error);
      toast.error('Failed to save Rush\'d treat');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[10001] flex items-center justify-center p-4"
          onClick={(e) => e.target === e.currentTarget && handleClose()}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", duration: 0.3 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative bg-gradient-to-r from-amber-500 to-amber-400 px-8 py-6">
              <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc=')] opacity-20"></div>
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring", bounce: 0.6 }}
                    className="h-12 w-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30"
                  >
                    <Crown className="h-6 w-6 text-white" />
                  </motion.div>
                  <div>
                    <motion.h2
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 }}
                      className="text-2xl font-bold text-white"
                    >
                      {initialTreat ? "Edit Rush'd Treat" : "New Rush'd Treat"}
                    </motion.h2>
                    <motion.p
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 }}
                      className="text-white/80 text-sm"
                    >
                      Delight Rush'd members with surprise freebies
                    </motion.p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClose}
                  disabled={saving}
                  className="text-white hover:bg-white/20 h-10 w-10 rounded-full"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-8 space-y-8 max-h-[calc(90vh-200px)] overflow-y-auto">
              <div className="space-y-4">
                <Label className="text-sm font-medium text-gray-900 flex items-center gap-2">
                  <Package className="h-4 w-4 text-amber-500" />
                  Free Item
                </Label>
                <Card className="p-4 border-dashed border-amber-200 bg-amber-50/50 space-y-4">
                  {selectedProduct ? (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <img
                          src={selectedProduct.image || selectedProduct.images?.[0] || '/placeholder.png'}
                          alt={selectedProduct.name}
                          className="w-12 h-12 rounded-md object-cover border border-amber-100"
                        />
                        <div>
                          <p className="font-medium text-gray-900">{selectedProduct.name}</p>
                          <p className="text-xs text-gray-500">ID: {selectedProduct.id}</p>
                        </div>
                      </div>
                      <Button type="button" variant="outline" size="sm" onClick={() => setShowProductPicker(true)}>
                        Change
                      </Button>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full flex items-center justify-center gap-2"
                      onClick={() => setShowProductPicker(true)}
                    >
                      <Sparkles className="w-4 h-4" />
                      Select free item
                    </Button>
                  )}

                  {showProductPicker && (
                    <div className="space-y-3">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                        <div className="flex items-center gap-2 flex-1 rounded-lg border border-amber-200 bg-white px-3 py-2">
                          <Search className="w-4 h-4 text-gray-400" />
                          <Input
                            placeholder="Search free items"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="border-none shadow-none focus-visible:ring-0 px-0"
                          />
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowProductPicker(false)}
                        >
                          Done
                        </Button>
                      </div>
                      <div className="max-h-64 overflow-y-auto space-y-2 pr-1">
                        {loadingProducts ? (
                          <div className="flex items-center justify-center py-6">
                            <Loader2 className="w-5 h-5 animate-spin text-amber-500" />
                          </div>
                        ) : filteredProducts.length === 0 ? (
                          <div className="text-sm text-gray-500 text-center py-4">
                            No free products found
                          </div>
                        ) : (
                          filteredProducts.map((product) => (
                            <Card
                              key={product.id}
                              className="p-3 hover:border-amber-300 cursor-pointer"
                              onClick={() => handleSelectProduct(product)}
                            >
                              <div className="flex items-center gap-3">
                                <img
                                  src={product.image || product.images?.[0] || '/placeholder.png'}
                                  alt={product.name}
                                  className="w-10 h-10 rounded-md object-cover"
                                />
                                <div className="flex-1">
                                  <p className="font-medium text-gray-900 text-sm">{product.name}</p>
                                  <p className="text-xs text-gray-500">{product.category || 'No category'}</p>
                                </div>
                                <Badge variant="outline" className="text-amber-600 border-amber-200">Free</Badge>
                              </div>
                            </Card>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </Card>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Quantity per member</Label>
                  <Input
                    type="number"
                    min={1}
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-200">
                    <Switch
                      checked={isActive}
                      onCheckedChange={setIsActive}
                    />
                    <span className="text-sm text-gray-700">
                      {isActive ? 'Active for Rush\'d members' : 'Temporarily disabled'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={handleClose} disabled={saving}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving || !selectedProduct}>
                  {saving ? 'Saving...' : initialTreat ? 'Update Treat' : 'Create Treat'}
                </Button>
              </div>
            </form>

          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
